import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormField, FormSelectField, FormSubmitButton, FormCancelButton, ErrorAlert, WarningAlert, withModalMount } from '@petseo/shared';
import { FormModal } from '@/components/FormModal';
import { externalReservationSchema, type ExternalReservationFormData } from '@/schemas/externalReservation.schema';
import { useCreateExternalReservation } from '../../hooks';
import { useEmployees } from '@/features/employees/hooks';
import { useServices, useBusinessHours, getBusinessHoursFit } from '@/features/service-vendors';
import { getEmployeeOptions, getServiceOptions, getTimeOptions } from '@/utils/mappers';
import { getDayOfWeekFromDateInput } from '@/utils/datetime/dayOfWeek';
import { addMinutes } from '@/utils/datetime/time';
import type { ServiceVendorId } from '@/services/api/petseo.client.wrapper';
import { createEmployeeId, createServiceVendorServiceId } from '@/services/api/petseo.client.wrapper';

interface ExternalReservationFormProps {
  serviceVendorId: ServiceVendorId;
  isOpen: boolean;
  onClose: () => void;
}

const timeOptions = getTimeOptions(15, 0, 24);

function ExternalReservationFormContent({ serviceVendorId, isOpen, onClose }: ExternalReservationFormProps) {
  const navigate = useNavigate();
  const createExternalReservation = useCreateExternalReservation();
  const { data: employees = [] } = useEmployees(serviceVendorId);
  const { data: services = [] } = useServices(serviceVendorId);

  const hasNoEmployees = employees.length === 0;
  const hasNoServices = services.length === 0;
  const hasMissingConfig = hasNoEmployees || hasNoServices;

  const employeeOptions = getEmployeeOptions(employees);
  const serviceOptions = getServiceOptions(services);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ExternalReservationFormData>({
    resolver: zodResolver(externalReservationSchema),
    defaultValues: {
      employeeId: '',
      serviceId: '',
      date: '',
      startTime: '',
      clientFirstName: '',
      clientLastName: '',
      clientEmail: '',
      clientPhone: '',
      note: '',
    },
  });

  const { data: businessHours } = useBusinessHours(serviceVendorId);
  const [selectedServiceId, selectedDate, selectedStartTime] = useWatch({ control, name: ['serviceId', 'date', 'startTime'] });

  const businessHoursFit = useMemo(() => {
    if (!selectedDate || !selectedStartTime) return 'unknown';

    const service = services.find((s) => String(s.id) === selectedServiceId);
    if (!service?.durationMinutes) return 'unknown';

    const dayOfWeek = getDayOfWeekFromDateInput(selectedDate);
    if (dayOfWeek === null) return 'unknown';

    return getBusinessHoursFit(businessHours, dayOfWeek, selectedStartTime, addMinutes(selectedStartTime, service.durationMinutes));
  }, [businessHours, services, selectedServiceId, selectedDate, selectedStartTime]);

  const handleClose = () => {
    reset();
    createExternalReservation.reset();
    onClose();
  };

  const onSubmit = async (data: ExternalReservationFormData) => {
    await createExternalReservation.mutateAsync({
      serviceVendorId,
      serviceVendorEmployeeId: createEmployeeId(data.employeeId),
      data: {
        serviceVendorServiceId: createServiceVendorServiceId(data.serviceId),
        date: data.date as unknown as Date,
        startTime: data.startTime,
        clientFirstName: data.clientFirstName,
        clientLastName: data.clientLastName,
        clientEmail: data.clientEmail,
        clientPhone: data.clientPhone,
        note: data.note || undefined,
      },
    });
    handleClose();
  };

  const isSubmitting = createExternalReservation.isPending;
  const error = createExternalReservation.error;

  return (
    <FormModal isOpen={isOpen} onClose={handleClose} title="Dodaj rezerwację zewnętrzną" size="lg">
      {hasMissingConfig ? (
        <div className="modal-body">
          {hasNoEmployees && (
            <WarningAlert
              title="Brak pracowników"
              message="Dodaj pracownika, aby móc tworzyć rezerwacje zewnętrzne."
              action={{
                label: 'Skonfiguruj',
                onClick: () => {
                  handleClose();
                  navigate(`/profiles/${serviceVendorId}?tab=employees`);
                },
              }}
            />
          )}
          {hasNoServices && (
            <WarningAlert
              title="Brak usług"
              message="Dodaj usługę, aby móc tworzyć rezerwacje zewnętrzne."
              action={{
                label: 'Skonfiguruj',
                onClick: () => {
                  handleClose();
                  navigate(`/profiles/${serviceVendorId}?tab=services`);
                },
              }}
            />
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body">
            {error && <ErrorAlert apiError={error} />}

            {businessHoursFit === 'closed' && (
              <WarningAlert
                title="Placówka zamknięta w tym dniu"
                message="Wg godzin otwarcia placówka jest w tym dniu zamknięta. Rezerwację można mimo to zapisać."
                className="mb-3"
              />
            )}

            {businessHoursFit === 'outside' && (
              <WarningAlert
                title="Termin poza godzinami otwarcia"
                message="Wybrany termin wykracza poza godziny otwarcia placówki. Rezerwację można mimo to zapisać."
                className="mb-3"
              />
            )}

            <FormSelectField
              control={control}
              name="employeeId"
              label="Pracownik"
              options={employeeOptions}
              placeholder="Wybierz pracownika"
              emptyText="Brak pracowników"
              required
              error={errors.employeeId}
            />

            <FormSelectField
              control={control}
              name="serviceId"
              label="Usługa"
              options={serviceOptions}
              placeholder="Wybierz usługę"
              emptyText="Brak usług"
              required
              error={errors.serviceId}
            />

            <div className="row">
              <div className="col-6">
                <FormField control={control} name="date" label="Data" type="date" required error={errors.date} />
              </div>
              <div className="col-6">
                <FormSelectField
                  control={control}
                  name="startTime"
                  label="Godzina rozpoczęcia"
                  options={timeOptions}
                  placeholder="Wybierz"
                  required
                  error={errors.startTime}
                />
              </div>
            </div>

            <hr className="my-3" />

            <div className="row">
              <div className="col-6">
                <FormField
                  control={control}
                  name="clientFirstName"
                  label="Imię klienta"
                  placeholder="np. Jan"
                  required
                  error={errors.clientFirstName}
                />
              </div>
              <div className="col-6">
                <FormField
                  control={control}
                  name="clientLastName"
                  label="Nazwisko klienta"
                  placeholder="np. Kowalski"
                  required
                  error={errors.clientLastName}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-6">
                <FormField
                  control={control}
                  name="clientEmail"
                  label="Email klienta"
                  placeholder="np. jan@example.com"
                  type="email"
                  required
                  error={errors.clientEmail}
                />
              </div>
              <div className="col-6">
                <FormField
                  control={control}
                  name="clientPhone"
                  label="Telefon klienta"
                  placeholder="np. 123456789"
                  required
                  error={errors.clientPhone}
                />
              </div>
            </div>

            <FormField control={control} name="note" label="Notatka" placeholder="Opcjonalna notatka..." error={errors.note} />
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <FormCancelButton onClick={handleClose} disabled={isSubmitting} />
            <FormSubmitButton isLoading={isSubmitting} disabled={isSubmitting} className="btn btn-primary">
              {isSubmitting ? 'Zapisywanie...' : 'Dodaj rezerwację'}
            </FormSubmitButton>
          </div>
        </form>
      )}
    </FormModal>
  );
}

export const ExternalReservationForm = withModalMount(ExternalReservationFormContent);
